import React from 'react';
import { Users, CheckCircle, MapPin } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SimpleTooltip } from '@/components/ui/SimpleTooltip';
import { trackCommunityBadgeClick } from '@/utils/analytics';
import { cn } from '@/lib/utils';

/**
 * Community Badge Component
 * 
 * Displays community contribution information for camps.
 * Automatically switches between community badge and Google Places badge
 * based on available data.
 * 
 * @param {Object} props
 * @param {Object} props.camp - Camp data with community information
 * @param {string} [props.variant] - Badge variant ('default' | 'frosted' | 'outline' | 'gray')
 * @param {string} [props.size] - Badge size ('xs' | 'sm' | 'md' | 'lg')
 * @param {boolean} [props.showVerification] - Show verification checkmark (default: true)
 * @param {boolean} [props.showTooltip] - Show tooltip on hover (default: true)
 * @param {string} [props.className] - Additional CSS classes
 */
export function CommunityBadge({
  camp,
  variant = 'default',
  size = 'md',
  showVerification = true,
  showTooltip = true,
  className,
}) {
  // Early return if camp is not provided
  if (!camp) {
    return null;
  }

  // Determine if this camp has community data
  const hasCommunityData = camp.user_contributed || camp.has_community_enhancements;

  // Icon size based on badge size
  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const iconSize = iconSizes[size];

  // Handle badge click for analytics
  const handleClick = () => {
    if (camp?.id) {
      trackCommunityBadgeClick({
        camp_id: camp.id,
        contribution_count: camp?.community_contribution_count || 0,
      });
    }
  };

  // If no community data, show Google Places badge
  if (!hasCommunityData) {
    const googleBadge = (
      <div 
        className={cn(
          "inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-600",
          className
        )}
        onClick={handleClick}
      >
        <MapPin className={cn('flex-shrink-0', iconSize)} />
        <span>Google Places</span>
      </div>
    );

    if (!showTooltip) return googleBadge;

    return (
      <SimpleTooltip content="Information from Google Places API">
        {googleBadge}
      </SimpleTooltip>
    );
  }

  // Community data exists
  const contributionCount = camp.community_contribution_count || 1;
  const verificationCount = camp.verification_count || 0;
  const confidence = camp.data_confidence_score || 50;

  // Determine badge text
  const badgeText = contributionCount === 1
    ? 'Parent Verified'
    : (contributionCount >= 10 ? '10+ parents' : `${contributionCount} parents`);

  // Create tooltip content
  const tooltipContent = (
    <div className="text-left">
      <div className="font-semibold mb-1">Community Verified</div>
      <div className="text-xs opacity-90">
        {contributionCount} {contributionCount === 1 ? 'parent has' : 'parents have'} contributed
      </div>
      {verificationCount > 0 && (
        <div className="text-xs opacity-90 mt-1">
          ✓ Verified by {verificationCount} {verificationCount === 1 ? 'user' : 'users'}
        </div>
      )}
      {confidence >= 70 && (
        <div className="text-xs opacity-90 mt-1">
          {confidence}% data quality
        </div>
      )}
    </div>
  );

  const badge = (
    <div 
      className={cn(
        "inline-flex items-center gap-1 px-2 py-1 bg-blue-100 rounded-full text-xs text-blue-700",
        className
      )}
      onClick={handleClick}
    >
      <Users className={cn('flex-shrink-0', iconSize)} />
      <span>{badgeText}</span>
      {showVerification && verificationCount > 0 && (
        <span className="ml-1">
          <CheckCircle
            className={cn('flex-shrink-0 text-green-600', iconSize)}
          />
        </span>
      )}
    </div>
  );

  if (!showTooltip) return badge;

  return (
    <SimpleTooltip
      content={tooltipContent}
      className="whitespace-normal max-w-xs"
    >
      {badge}
    </SimpleTooltip>
  );
};

/**
 * Simple Community Badge
 * Quick usage wrapper with defaults
 */
export function SimpleCommunityBadge({ camp }) {
  return <CommunityBadge camp={camp} />;
}

/**
 * Community Field Indicator Component
 * 
 * Shows a color-coded checkmark icon next to specific fields that have
 * community verification. Appears inline with field values.
 * 
 * @param {Object} props
 * @param {string} props.field - Field name ('cost_details' | 'schedule_details' | 'camp_start_date' | 'session_start_time')
 * @param {Object} props.camp - Camp data with user fields and confidence levels
 * @param {boolean} [props.showTooltip] - Show tooltip on hover (default: true)
 * @param {string} [props.size] - Icon size ('sm' | 'md')
 */
export function CommunityFieldIndicator({
  field,
  camp,
  showTooltip = true,
  size = 'md',
}) {
  // Early return if camp is not provided
  if (!camp) {
    return null;
  }

  // Check if this specific field has user data
  // Support both 'user_${field}' and direct field access
  const fieldData = camp[`user_${field}`] || camp[field];
  const hasUserData = fieldData;

  if (!hasUserData) return null;

  // Determine confidence level
  let confidence = 'medium';

  // Map field names to confidence fields
  if (field === 'cost_details') {
    confidence = camp.cost_confidence || 'medium';
  } else if (field === 'schedule_details' || field === 'session_start_time') {
    confidence = camp.schedule_confidence || 'medium';
  } else if (field === 'camp_start_date' || field === 'camp_end_date') {
    confidence = camp.dates_confidence || 'medium';
  }

  // Color based on confidence
  const confidenceColors = {
    high: 'text-green-600',
    medium: 'text-blue-600',
    low: 'text-amber-600',
  };

  // Icon size
  const iconSizes = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
  };

  // Tooltip content
  const confidenceLabels = {
    high: 'High',
    medium: 'Medium',
    low: 'Low',
  };

  const tooltipContent = `Community-verified (${confidenceLabels[confidence]} confidence)`;

  const icon = (
    <CheckCircle
      className={cn(
        'inline-block flex-shrink-0',
        confidenceColors[confidence],
        iconSizes[size]
      )}
      aria-label={`${confidenceLabels[confidence]} confidence - Community verified`}
    />
  );

  if (!showTooltip) return icon;

  return (
    <SimpleTooltip content={tooltipContent}>
      {icon}
    </SimpleTooltip>
  );
}

/**
 * Field Verification Badge Component
 * 
 * Shows a badge with text indicating verification status for a field.
 * 
 * @param {Object} props
 * @param {string} props.field - Field name
 * @param {Object} props.camp - Camp data
 */
export function FieldVerificationBadge({
  field,
  camp,
}) {
  if (!camp) {
    return null;
  }

  const hasUserData = camp[`user_${field}`];

  if (!hasUserData) return null;

  let confidence = 'medium';
  if (field === 'cost_details') {
    confidence = camp.cost_confidence || 'medium';
  } else if (field.includes('schedule') || field.includes('time')) {
    confidence = camp.schedule_confidence || 'medium';
  } else if (field.includes('date')) {
    confidence = camp.dates_confidence || 'medium';
  }

  const labels = {
    high: 'Verified',
    medium: 'Community Data',
    low: 'Unverified',
  };

  const variants = {
    high: 'success',
    medium: 'info',
    low: 'warning',
  };

  return (
    <Badge
      variant={variants[confidence]}
      size="xs"
    >
      <CheckCircle className="w-3 h-3" />
      {labels[confidence]}
    </Badge>
  );
}

/**
 * Get all community verified fields for a camp
 * 
 * @param {Object} camp - Camp data
 * @returns {Array} Array of verified field objects with name and confidence
 */
export function getCommunityVerifiedFields(camp) {
  if (!camp) {
    return [];
  }

  const fields = [];

  if (camp.user_cost_details) {
    fields.push({
      name: 'Cost',
      confidence: camp.cost_confidence || 'medium',
    });
  }

  if (camp.user_schedule_details || camp.user_session_start_time) {
    fields.push({
      name: 'Schedule',
      confidence: camp.schedule_confidence || 'medium',
    });
  }

  if (camp.user_camp_start_date) {
    fields.push({
      name: 'Dates',
      confidence: camp.dates_confidence || 'medium',
    });
  }

  return fields;
}

// Default export for backward compatibility
export default CommunityBadge;

